import React, { useState } from 'react';
import { Modal, Input } from 'antd';
import { useLogin } from './handlers';

const Login: React.FC = () => {
  const {
    form, loading, error, rememberMe,
    showPwdModal, newPwd, confirmPwd, pwdLoading,
    handleChange, handleSubmit, handleSetPassword,
    setRememberMe, setNewPwd, setConfirmPwd,
  } = useLogin();

  // Show / hide password toggle. Local UI state — never persisted, no need to
  // hoist into the handler. The Change-Password and first-time-setup forms
  // use Ant Design's Input.Password which already has this built in; only the
  // login form's plain <input> needs the manual toggle.
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSubmit(e as any);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="e.dye" className="mx-auto mb-4" style={{ height: 100 }} />
          <p className="text-gray-600 mt-2">e.dye BI智慧平台</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">帳號</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <input id="username" type="text" value={form.username} onChange={e => handleChange('username', e.target.value)} onKeyPress={handleKeyPress}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="請輸入帳號" autoComplete="username" disabled={loading} />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">密碼</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <input id="password" type={showPassword ? 'text' : 'password'} value={form.password} onChange={e => handleChange('password', e.target.value)} onKeyPress={handleKeyPress}
                className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="請輸入密碼" autoComplete="current-password" disabled={loading} />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                disabled={loading}
                aria-label={showPassword ? '隱藏密碼' : '顯示密碼'}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600 disabled:cursor-not-allowed"
              >
                {showPassword ? (
                  // Eye-slashed: password is currently visible — click to hide.
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                  </svg>
                ) : (
                  // Eye: password is currently hidden — click to reveal.
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input id="remember-me" type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" disabled={loading} />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">記住我</label>
            </div>
            <div className="text-sm">
              <a href="#" onClick={e => { e.preventDefault(); setShowForgotModal(true); }} className="font-medium text-blue-600 hover:text-blue-500">忘記密碼？</a>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">{error}</div>
          )}

          <div>
            <button type="submit" disabled={loading}
              className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'}`}>
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  登入中...
                </>
              ) : '登入'}
            </button>
          </div>
        </form>

        <div className="mt-6 text-center text-sm text-gray-600">
          <p>&copy; 2026 e.dye. All rights reserved.</p>
        </div>
      </div>

      <Modal open={showForgotModal} title="忘記密碼" onCancel={() => setShowForgotModal(false)}
        onOk={() => setShowForgotModal(false)} okText="我知道了"
        cancelButtonProps={{ style: { display: 'none' } }}>
        <p style={{ marginBottom: 0 }}>目前重設密碼請先透過<strong>錢佳</strong>向管理員反應，實際重設步驟待討論。</p>
      </Modal>

      <Modal open={showPwdModal} title="首次登入 — 請設定新密碼" closable={false} maskClosable={false} keyboard={false}
        okText={pwdLoading ? '設定中...' : '確認設定'} cancelButtonProps={{ style: { display: 'none' } }}
        onOk={handleSetPassword} confirmLoading={pwdLoading}>
        <p style={{ marginBottom: 16, color: '#666' }}>為了帳號安全，首次登入需要設定新密碼（至少 6 碼）。</p>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>新密碼</label>
          <Input.Password placeholder="請輸入新密碼（至少 6 碼）" value={newPwd} onChange={e => setNewPwd(e.target.value)}
            onPressEnter={() => document.getElementById('confirm-pwd')?.focus()} />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>確認密碼</label>
          <Input.Password id="confirm-pwd" placeholder="再次輸入新密碼" value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)}
            onPressEnter={handleSetPassword} />
        </div>
      </Modal>
    </div>
  );
};

export default Login;
